import React, { FC, useState, useMemo, useEffect } from 'react'
import { Badge, Dropdown, Button, Select, Flex, Typography } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import type { STRFilters } from './STRFilterBar'
import styles from './STRTablePage.module.scss'

const { Text } = Typography

export type HostProfileFilter = 'show_all' | 'equal' | 'not_equal'

interface STRMoreFiltersPopoverProps {
  filters: STRFilters
  onFiltersChange: (filters: STRFilters) => void
}

const HOST_PROFILE_OPTIONS = [
  { label: 'Show All', value: 'show_all' },
  { label: 'Host is the owner', value: 'equal' },
  { label: 'Host is not the owner', value: 'not_equal' },
]

const TAXPAYER_TYPE_OPTIONS = [
  { label: 'Resident', value: 'Resident' },
  { label: 'Non Resident', value: 'Non Resident' },
]

const SELECT_WIDTH = 200

const MORE_FILTER_KEYS: (keyof STRFilters)[] = [
  'hostProfileFilter',
  'taxpayerType',
  'activeListing',
  'tinStatus',
]

const STRMoreFiltersPopover: FC<STRMoreFiltersPopoverProps> = ({ filters, onFiltersChange }) => {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Partial<STRFilters>>({})

  useEffect(() => {
    if (open) {
      setDraft({
        hostProfileFilter: filters.hostProfileFilter,
        taxpayerType: filters.taxpayerType,
        activeListing: filters.activeListing,
        tinStatus: filters.tinStatus,
      })
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const activeCount = useMemo(() => {
    let count = 0
    if (filters.hostProfileFilter && filters.hostProfileFilter !== 'show_all') count++
    if (filters.taxpayerType?.length) count++
    if (filters.activeListing != null) count++
    if (filters.tinStatus) count++
    return count
  }, [filters])

  const isActive = activeCount > 0

  const handleDraftChange = (key: keyof STRFilters, value: unknown) => {
    setDraft(prev => ({ ...prev, [key]: value }))
  }

  const handleSubmit = () => {
    onFiltersChange({ ...filters, ...draft })
    setOpen(false)
  }

  const dropdownRender = () => (
    <div className={styles.moreFiltersDropdown}>
      <div className={styles.moreFiltersHeader}>
        <Text strong style={{ fontSize: 16 }}>More Filters</Text>
        <CloseOutlined style={{ color: '#00000073', fontSize: 14, cursor: 'pointer' }} onClick={() => setOpen(false)} />
      </div>

      <div className={styles.moreFiltersBody}>
        <Flex align='center' justify='space-between' className={styles.moreFiltersRow}>
          <Text>Host Profile</Text>
          <Select
            value={draft.hostProfileFilter || 'show_all'}
            onChange={val => handleDraftChange('hostProfileFilter', val)}
            options={HOST_PROFILE_OPTIONS}
            style={{ width: SELECT_WIDTH }}
          />
        </Flex>

        <Flex align='center' justify='space-between' className={styles.moreFiltersRow}>
          <Text>Taxpayer Type</Text>
          <Select
            mode='multiple'
            placeholder='Select'
            allowClear
            value={draft.taxpayerType}
            onChange={val => handleDraftChange('taxpayerType', val)}
            options={TAXPAYER_TYPE_OPTIONS}
            style={{ width: SELECT_WIDTH }}
          />
        </Flex>

        <Flex align='center' justify='space-between' className={styles.moreFiltersRow}>
          <Text>Active Listing</Text>
          <Select
            placeholder='Select'
            allowClear
            value={draft.activeListing != null ? String(draft.activeListing) : undefined}
            onChange={val => handleDraftChange('activeListing', val != null ? Number(val) : null)}
            options={[
              { label: '1+', value: '1' },
              { label: '2+', value: '2' },
              { label: '5+', value: '5' },
              { label: '10+', value: '10' },
            ]}
            style={{ width: SELECT_WIDTH }}
          />
        </Flex>

        <Flex align='center' justify='space-between' className={styles.moreFiltersRow}>
          <Text>TIN Status</Text>
          <Select
            placeholder='Select'
            allowClear
            value={draft.tinStatus}
            onChange={val => handleDraftChange('tinStatus', val)}
            options={[
              { label: 'Matched', value: 'Matched' },
              { label: 'Unmatched', value: 'Unmatched' },
            ]}
            style={{ width: SELECT_WIDTH }}
          />
        </Flex>

        <Flex justify='flex-end' style={{ marginTop: 8 }}>
          <Button type='primary' onClick={handleSubmit}>
            Submit
          </Button>
        </Flex>
      </div>
    </div>
  )

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      popupRender={dropdownRender}
      trigger={['click']}
      placement='bottomRight'
    >
      <Badge count={isActive ? activeCount : 0} offset={[-2, -2]} color='#FF6161' size='small'>
        <Button
          icon={
            <svg width='18' height='13' viewBox='0 0 18 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M7 13.125H11V11.25H7V13.125ZM3 7.5H15V5.625H3V7.5ZM0 0V1.875H18V0H0Z'
                fill={isActive ? '#fff' : '#1890FF'}
              />
            </svg>
          }
          style={{
            width: 32,
            height: 32,
            border: '1px solid #1890FF',
            borderRadius: 4,
            padding: 0,
            background: isActive ? '#1890FF' : '#fff',
          }}
        />
      </Badge>
    </Dropdown>
  )
}

export default STRMoreFiltersPopover
